// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web — features/financeiro/services/fluxoCaixaGerencialService.ts
//  Fluxo de Caixa Gerencial (paridade do desktop FrmFluxoCaixaSync):
//  matriz seção→conta×mês + totalizadores BI + drill-down diário.
// ════════════════════════════════════════════════════════════════════════════

import { apiClient } from '@/lib/apiClient';

const BASE = '/api/financeiro/fluxo-caixa-gerencial';

export interface FluxoGerencialMes {
  ano: number;
  mes: number;
  label: string;
}

export interface FluxoGerencialConta {
  contaId: number;
  gerencial: boolean;
  codigo: string;
  descricao: string;
  valores: number[];
  total: number;
}

export interface FluxoGerencialSecao {
  codigo: string;
  nome: string;
  valores: number[];
  total: number;
  contas: FluxoGerencialConta[];
}

export interface FluxoGerencialResumoMes {
  ano: number;
  mes: number;
  label: string;
  receita: number;
  despesaOperacional: number;
  resultadoLiquido: number;
}

export interface TotalizadoresFluxoCaixa {
  receita: number;
  custosVariaveis: number;
  margemContribuicao: number;
  despesasFixas: number;
  loai: number;
  investimentos: number;
  despesaOperacionalTotal: number;
  lucroOperacional: number;
  entradaNaoOperacional: number;
  saidasNaoOperacional: number;
  movimentoNaoOperacional: number;
  resultadoLiquido: number;
  peai: number;
  pei: number;
  perReceitas: number;
  perCustosVariaveis: number;
  perMargemContrib: number;
  perDespesasFixas: number;
  perLOAI: number;
  perInvestimento: number;
  perDespesaOperacional: number;
  perLucroOperacional: number;
  perMovimentoNaoOperacional: number;
  perResultadoLiquido: number;
  perPEAI: number;
  perPEI: number;
  resultadoPositivo: boolean;
}

export interface FluxoCaixaGerencial {
  dataInicio: string;
  dataFim: string;
  meses: FluxoGerencialMes[];
  secoes: FluxoGerencialSecao[];
  resumoMensal: FluxoGerencialResumoMes[];
  totalizadores: TotalizadoresFluxoCaixa;
}

export interface FluxoGerencialDetalheDia {
  data: string;
  creditos: number;
  debitos: number;
  valor: number;
  lancamentos: number;
}

export interface FluxoGerencialDetalhe {
  contaId: number;
  codigo: string;
  descricao: string;
  dataInicio: string;
  dataFim: string;
  total: number;
  dias: FluxoGerencialDetalheDia[];
}

export interface ReprocessoResultado {
  linhasRemovidas: number;
  geradasContasPagar: number;
  geradasContasReceber: number;
  geradasCaixa: number;
}

export interface ReprocessarParams {
  dtInicio: string;
  dtFim: string;
  contasPagar: boolean;
  contasReceber: boolean;
  caixa: boolean;
}

export const fluxoCaixaGerencialService = {
  async obter(dtInicio: string, dtFim: string): Promise<FluxoCaixaGerencial> {
    const { data } = await apiClient.get<FluxoCaixaGerencial>(BASE, {
      params: { dtInicio, dtFim },
    });
    return data;
  },

  async reprocessar(params: ReprocessarParams): Promise<ReprocessoResultado> {
    const { data } = await apiClient.post<ReprocessoResultado>(`${BASE}/reprocessar`, params);
    return data;
  },

  async detalhe(
    contaId: number,
    gerencial: boolean,
    dtInicio: string,
    dtFim: string,
  ): Promise<FluxoGerencialDetalhe> {
    const { data } = await apiClient.get<FluxoGerencialDetalhe>(`${BASE}/detalhe`, {
      params: { contaId, gerencial, dtInicio, dtFim },
    });
    return data;
  },
};

export const FLUXO_GERENCIAL_EXPORT_ENDPOINT = `${BASE}/exportar`;
